import { format } from "date-fns";
import { useCallback, useRef, useState } from "react";
import EventModal from "./EventModal";
import { PlanningEvent } from "./PlanningEvent";
import { isAfterParsedDate } from "./Planning3";
import { computeOverlapLayout } from "./overlapLayout";

const minutesToTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const PlanningWeekView = ({
  events,
  timeSlots,
  handleClick,
  handleOpenModal,
  handleCloseModal,
  open,
  highligtedEvent,
  day_view,
  daysOfWeek,
  weekDates,
  height,
  events_colors,
  pack_short_dict,
  simplified_view,
  masterData,
  orgData,
  userData,
  selected_operators,
  onDelete,
  navigateWorkspace,
  workspaceData,
  getYPosition,
  timeToMinutes,
  temporary,
  setTemporary,
  onEventTimeChange,
  enable_drag = false,
  enable_resize = false,
}) => {
  const [dragState, setDragState] = useState(null);
  const wasDraggingRef = useRef(false);
  const pixelsPerMinute = (height * 0.0772) / 60;
  const snap15px = pixelsPerMinute * 15;
  const columnRefMap = useRef({});

  const handleDragStart = useCallback(
    (e, event, mode, originDayIndex) => {
      if (mode === "resize") {
        e.preventDefault();
        e.stopPropagation();
      }
      const startX = e.clientX;
      const startY = e.clientY;
      const colEl = columnRefMap.current[originDayIndex];
      const colWidth = colEl ? colEl.getBoundingClientRect().width : 0;
      const info = {
        eventId: event.id,
        startX,
        startY,
        originalStartMinutes: timeToMinutes(event.startTime),
        originalEndMinutes: timeToMinutes(event.endTime),
        mode,
        activated: false,
        originDayIndex,
        colWidth,
      };

      const onMove = (moveEvent) => {
        const deltaY = moveEvent.clientY - startY;
        const deltaX = moveEvent.clientX - startX;
        if (!info.activated && (Math.abs(deltaY) > 5 || Math.abs(deltaX) > 5)) {
          info.activated = true;
          wasDraggingRef.current = true;
        }
        if (info.activated) {
          const snappedDeltaY = Math.round(deltaY / snap15px) * snap15px;
          const dayShift = colWidth > 0 ? Math.round(deltaX / colWidth) : 0;
          setDragState({ ...info, deltaY: snappedDeltaY, deltaX, dayShift });
        }
      };

      const onUp = (upEvent) => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);

        if (info.activated && onEventTimeChange) {
          const deltaY = upEvent.clientY - startY;
          const deltaX = upEvent.clientX - startX;
          const minutesDelta = Math.round(deltaY / pixelsPerMinute / 15) * 15;
          const dayShift = colWidth > 0 ? Math.round(deltaX / colWidth) : 0;

          if (Math.abs(minutesDelta) > 0 || dayShift !== 0) {
            if (info.mode === "drag") {
              onEventTimeChange(
                info.eventId,
                info.originalStartMinutes + minutesDelta,
                info.originalEndMinutes + minutesDelta,
                dayShift
              );
            } else {
              const newEnd = info.originalEndMinutes + minutesDelta;
              if (newEnd > info.originalStartMinutes + 15) {
                onEventTimeChange(info.eventId, info.originalStartMinutes, newEnd);
              }
            }
          }
        }

        setDragState(null);
        setTimeout(() => { wasDraggingRef.current = false; }, 50);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [timeToMinutes, pixelsPerMinute, snap15px, onEventTimeChange]
  );

  const getDisplayEvent = (event) => {
    if (!dragState || dragState.eventId !== event.id) return event;
    const minutesDelta = Math.round(dragState.deltaY / snap15px) * 15;

    if (dragState.mode === "resize") {
      const newEnd = dragState.originalEndMinutes + minutesDelta;
      if (newEnd <= dragState.originalStartMinutes + 15) return event;
      return { ...event, endTime: minutesToTime(newEnd) };
    }
    if (minutesDelta !== 0) {
      return {
        ...event,
        startTime: minutesToTime(dragState.originalStartMinutes + minutesDelta),
        endTime: minutesToTime(dragState.originalEndMinutes + minutesDelta),
      };
    }
    return event;
  };

  return (
    <>
      {day_view === null &&
        daysOfWeek.map((day, day_index) => (
          <div
            key={day_index}
            ref={(el) => { columnRefMap.current[day_index] = el; }}
            className="bg-transparent border-l-1 border-shadow_5  flex flex-col  w-1/5  font-light min-h-fit p-1 text-h4 text-black justify-center"
          >
            <div
              className=" gap-1 w-full h-c500 relative "
              style={{
                height: `${height}px`,
              }}
            >
              <div
                className="absolute w-full max-h-full "
                style={{
                  top: "6px",
                }}
              >
                {timeSlots.map((hour, timeindex) => (
                  <div
                    key={timeindex}
                    style={{
                      height: `${height * 0.0772}px`,
                    }}
                    onClick={(e) => {
                      handleClick(e, day_index, null, hour);
                    }}
                    className=" w-full border-t-1   border-shadow_5  select-none relative flex-col hover:border-2 hover:border-wshadow_50 hover:bg-shadow_3 opacity-100 font-light p-2 justify-start"
                  ></div>
                ))}{" "}
              </div>

              {(() => {
                const dayEvents = events
                  .filter(
                    (event) =>
                      (event.weekdate === day &&
                        (event.frequency || "") !== "daily") ||
                      event.frequency === "daily"
                  )
                  .filter((event) =>
                    event.exception_dates && event.frequency
                      ? !(event.exception_dates || []).includes(
                          format(weekDates[day_index], "dd/MM/yyyy")
                        )
                      : event.upper_limit_date &&
                          isAfterParsedDate(
                            format(weekDates[day_index], "dd/MM/yyyy"),
                            event.upper_limit_date
                          )
                        ? false
                        : true
                  )
                  .filter((event) => {
                    const noOperator = selected_operators.includes("no_operator");
                    const allOperators = selected_operators.includes("all");
                    const members = event.members_id ?? [];

                    if (allOperators) return true;
                    if (noOperator && members.length === 0) return true;
                    return members.some((member) =>
                      selected_operators.includes(member)
                    );
                  })
                  .sort((a, b) => a.start - b.start);

                const layout = computeOverlapLayout(dayEvents, timeToMinutes);

                return dayEvents.map((event, index) => {
                  const overlap = layout.get(event.id) || { column: 0, totalColumns: 1 };
                  const widthPercent = 100 / overlap.totalColumns;
                  const leftPercent = overlap.column * widthPercent;
                  const isDragging = dragState?.eventId === event.id && dragState.mode === "drag";
                  const isResizing = dragState?.eventId === event.id && dragState.mode === "resize";
                  const displayEvent = getDisplayEvent(event);

                  const dayShiftPx = isDragging && dragState.dayShift ? dragState.dayShift * (dragState.colWidth || 0) : 0;

                  return (
                    <div
                      key={index}
                      className="absolute group"
                      style={{
                        top: `${getYPosition(timeToMinutes(enable_drag ? displayEvent.startTime : event.startTime))}rem`,
                        zIndex: isDragging || isResizing ? 100 : open[event.id] ? 10 : highligtedEvent === event.id ? 5 : 1,
                        width: `${widthPercent}%`,
                        left: `${leftPercent}%`,
                        transform: enable_drag && isDragging ? `translateX(${dayShiftPx}px)` : undefined,
                        opacity: isDragging || isResizing ? 0.85 : 1,
                        cursor: enable_drag ? (isDragging ? "grabbing" : "grab") : undefined,
                        transition: dragState ? "none" : undefined,
                        userSelect: enable_drag ? "none" : undefined,
                      }}
                      onMouseDown={enable_drag ? (e) => {
                        if (e.button !== 0) return;
                        if (Object.values(open).some(Boolean)) return;
                        handleDragStart(e, event, "drag", day_index);
                      } : undefined}
                      onClickCapture={enable_drag ? (e) => {
                        if (wasDraggingRef.current) {
                          e.stopPropagation();
                          e.preventDefault();
                        }
                      } : undefined}
                    >
                      <div style={{ pointerEvents: isDragging || isResizing ? "none" : "auto" }}>
                        <EventModal
                          parsed_date={format(weekDates[day_index], "dd/MM/yyyy")}
                          event={event}
                          setEvent={() => {}}
                          setModalOpen={() => handleCloseModal(event.id)}
                          orgData={orgData}
                          userData={userData}
                          onDelete={(e) => {
                            onDelete(e);
                          }}
                          onUpdate={() => {}}
                          open={open[event.id]}
                          setOpen={(e) => {
                            handleOpenModal(event.id);
                          }}
                          navigateWorkspace={navigateWorkspace}
                          workspaceData={workspaceData}
                          temporary={temporary}
                          setTemporary={setTemporary}
                        >
                          <PlanningEvent
                            event={displayEvent}
                            events_colors={events_colors}
                            pack_short_dict={pack_short_dict}
                            highligtedEvent={highligtedEvent}
                            height={height}
                            simplified_view={simplified_view}
                            masterData={masterData}
                          ></PlanningEvent>
                        </EventModal>
                      </div>
                      {enable_resize && (
                        <div
                          className="absolute bottom-0 left-1 right-1 h-2 cursor-s-resize rounded-b-md opacity-0 group-hover:opacity-100 hover:!opacity-100 transition-opacity"
                          style={{ backgroundColor: "rgba(0,0,0,0.15)" }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            handleDragStart(e, event, "resize", day_index);
                          }}
                        />
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        ))}
    </>
  );
};
